import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

const beforeAfterPairs = [
  {
    id: 'pair-1',
    before: {
      src: 'https://readdy.ai/api/search-image?query=Hyperrealistic%20beauty%20portrait%20close%20up%20of%20the%20same%20dark%20skinned%20Black%20African%20woman%20with%20very%20short%20natural%20afro%20hair%2C%20facing%20straight%20to%20camera%2C%20studio%20beauty%20lighting%20with%20soft%20diffused%20key%20light%20from%20front%2C%20neutral%20light%20beige%20solid%20background%2C%20skin%20shows%20visible%20dehydration%20dullness%20uneven%20texture%20rough%20patches%20flaking%20dryness%20on%20cheeks%20and%20forehead%2C%20fine%20lines%20visible%2C%20no%20makeup%2C%20dry%20lips%2C%20tired%20appearance%2C%20professional%20dermatological%20before%20photo%20style%2C%20extreme%20detail%20skin%20texture%20pores%2C%20photorealistic%2C%20medical%20grade%20photography%2C%20consistent%20facial%20structure&nocache=true&width=600&height=750&seq=kds-ba-dark-before&orientation=portrait',
      label: 'Avant',
    },
    after: {
      src: 'https://public.readdy.ai/ai/img_res/edited_74b0d570ad6bb007f393a659e1707ff4_94ef430e.jpg',
      label: 'Après',
    },
  },
  {
    id: 'pair-2',
    before: {
      src: 'https://readdy.ai/api/search-image?query=Hyperrealistic%20beauty%20portrait%20close%20up%20of%20the%20same%20light%20skinned%20Black%20African%20woman%20with%20sleek%20natural%20braids%20pulled%20back%2C%20facing%20straight%20to%20camera%2C%20studio%20beauty%20lighting%20with%20soft%20diffused%20key%20light%20from%20front%2C%20neutral%20light%20beige%20solid%20background%2C%20skin%20shows%20visible%20dehydration%20dullness%20uneven%20texture%20rough%20patches%20flaking%20dryness%20on%20cheeks%20and%20forehead%2C%20fine%20lines%20visible%2C%20no%20makeup%2C%20dry%20lips%2C%20tired%20appearance%2C%20professional%20dermatological%20before%20photo%20style%2C%20extreme%20detail%20skin%20texture%20pores%2C%20photorealistic%2C%20medical%20grade%20photography%2C%20consistent%20facial%20structure&nocache=true&width=600&height=750&seq=kds-ba-light-before&orientation=portrait',
      label: 'Avant',
    },
    after: {
      src: 'https://public.readdy.ai/ai/img_res/edited_ed69d0b9311319cbe8a81143e1b73ea7_6a7bc0b1.jpg',
      label: 'Après',
    },
  },
];

export default function GallerySection() {
  const { t } = useTranslation();
  const sectionRef = useRef<HTMLDivElement>(null);
  const [activePair, setActivePair] = useState(0);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const elements = entry.target.querySelectorAll('.gallery-reveal');
            elements.forEach((el, i) => {
              setTimeout(() => { el.classList.add('visible'); }, i * 200);
            });
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  // Auto-rotate pairs every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setActivePair((prev) => (prev + 1) % beforeAfterPairs.length);
    }, 5000);
    return () => clearInterval(interval);
  }, []);

  const currentPair = beforeAfterPairs[activePair];

  return (
    <section ref={sectionRef} className="w-full py-16 md:py-24 bg-background-100">
      <div className="w-full px-4 md:px-8 lg:px-12">
        <div className="text-center mb-10 md:mb-14">
          <span className="inline-block px-4 py-1.5 text-xs font-medium bg-accent-100 text-accent-700 rounded-full mb-4 tracking-wider uppercase">
            Résultats visibles
          </span>
          <h2 className="font-heading text-3xl md:text-4xl lg:text-5xl text-foreground-800 mb-4">
            Avant / Après
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-xl mx-auto">
            Des transformations réelles obtenues grâce à nos soins professionnels. Voyez la différence par vous-même.
          </p>
        </div>

        {/* Before/After comparison */}
        <div className="gallery-reveal opacity-0 translate-y-6 transition-all duration-700 max-w-4xl mx-auto">
          <div className="relative bg-background-50 rounded-3xl overflow-hidden border border-background-200/40">
            {/* Comparison grid */}
            <div className="grid grid-cols-1 md:grid-cols-2">
              {/* Before */}
              <div className="relative group cursor-pointer">
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={currentPair.before.src}
                    alt="Avant le soin"
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.02]"
                  />
                </div>
                {/* Label */}
                <div className="absolute top-4 left-4">
                  <span className="inline-block px-4 py-1.5 text-xs font-bold bg-foreground-950/70 text-background-50 rounded-full uppercase tracking-wider backdrop-blur-sm">
                    {currentPair.before.label}
                  </span>
                </div>
                {/* Skin concern indicators */}
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2.5 py-1 text-[10px] font-medium bg-background-50/80 backdrop-blur-sm text-foreground-600 rounded-full">Peau déshydratée</span>
                    <span className="px-2.5 py-1 text-[10px] font-medium bg-background-50/80 backdrop-blur-sm text-foreground-600 rounded-full">Teint terne</span>
                    <span className="px-2.5 py-1 text-[10px] font-medium bg-background-50/80 backdrop-blur-sm text-foreground-600 rounded-full">Rugosité</span>
                  </div>
                </div>
              </div>

              {/* After */}
              <div className="relative group cursor-pointer">
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={currentPair.after.src}
                    alt="Après le soin"
                    className="w-full h-full object-cover object-top transition-transform duration-700 group-hover:scale-[1.02]"
                  />
                </div>
                {/* Label */}
                <div className="absolute top-4 right-4">
                  <span className="inline-block px-4 py-1.5 text-xs font-bold bg-secondary-600 text-background-50 rounded-full uppercase tracking-wider">
                    {currentPair.after.label}
                  </span>
                </div>
                {/* Result indicators */}
                <div className="absolute bottom-4 left-4 right-4">
                  <div className="flex flex-wrap gap-2">
                    <span className="px-2.5 py-1 text-[10px] font-medium bg-background-50/80 backdrop-blur-sm text-secondary-700 rounded-full">Hydratée</span>
                    <span className="px-2.5 py-1 text-[10px] font-medium bg-background-50/80 backdrop-blur-sm text-secondary-700 rounded-full">Éclatante</span>
                    <span className="px-2.5 py-1 text-[10px] font-medium bg-background-50/80 backdrop-blur-sm text-secondary-700 rounded-full">Lisse</span>
                  </div>
                </div>
              </div>
            </div>

            {/* VS divider */}
            <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-10 hidden md:flex">
              <div className="w-12 h-12 rounded-full bg-background-50 shadow-lg border border-background-200/60 flex items-center justify-center">
                <span className="font-heading text-xs font-bold text-foreground-400">VS</span>
              </div>
            </div>

            {/* Pair navigation dots */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 flex items-center gap-2">
              {beforeAfterPairs.map((_, i) => (
                <button
                  key={i}
                  onClick={() => setActivePair(i)}
                  className={`w-2.5 h-2.5 rounded-full transition-all duration-300 cursor-pointer ${
                    i === activePair
                      ? 'bg-primary-500 w-7'
                      : 'bg-background-50/60 hover:bg-background-50/90'
                  }`}
                  aria-label={`Paire ${i + 1}`}
                />
              ))}
            </div>
          </div>

          {/* Pair info */}
          <p className="text-center text-xs text-foreground-500 mt-4">
            {activePair === 0 ? 'Soin du visage hydratant · Peau foncée' : 'Soin du visage hydratant · Peau claire'}
            {' · '}Résultats après 1 séance
          </p>
        </div>
      </div>
    </section>
  );
}